import { can, type OrganisationRole } from '@evw/domain';
import { Button, Card } from '@evw/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type { Organisation } from '../../lib/types';
import { updateOrganisation } from '../tenancy/api';

export function OrganisationProfile({
  organisation,
  role
}: {
  organisation: Organisation;
  role: OrganisationRole;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(organisation.name);
  const [timeZone, setTimeZone] = useState(organisation.time_zone);
  const [taxNumber, setTaxNumber] = useState(organisation.tax_registration_number ?? '');
  const editable = can(role, 'update', 'organisation');
  const mutation = useMutation({
    mutationFn: () =>
      updateOrganisation(organisation.id, {
        name: name.trim(),
        time_zone: timeZone.trim(),
        tax_registration_number: taxNumber.trim() || null
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['memberships'] })
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <Card>
      <div className="section-heading">
        <div>
          <h2>Organisation profile</h2>
          <p className="muted">Core business settings.</p>
        </div>
        <span className="role-pill">{role}</span>
      </div>
      <form className="stack" onSubmit={submit}>
        <label>
          Name
          <input
            disabled={!editable}
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Time zone
          <input
            disabled={!editable}
            required
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
          />
        </label>
        <label>
          Tax registration number
          <input
            disabled={!editable}
            value={taxNumber}
            onChange={(e) => setTaxNumber(e.target.value)}
          />
        </label>
        <div className="detail-grid">
          <div>
            <span>Currency</span>
            <strong>{organisation.default_currency}</strong>
          </div>
          <div>
            <span>Minimum deposit</span>
            <strong>{organisation.deposit_bps / 100}%</strong>
          </div>
        </div>
        {editable ? (
          <Button disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save profile'}
          </Button>
        ) : (
          <p className="muted">Your role has read-only access to this profile.</p>
        )}
        {mutation.error ? (
          <p className="error" role="alert">
            {mutation.error.message}
          </p>
        ) : null}
        {mutation.isSuccess ? (
          <p className="success" role="status">
            Profile saved.
          </p>
        ) : null}
      </form>
    </Card>
  );
}
